import React from 'react';
import { Route, IndexRoute } from 'react-router';

import {
    accountStatusChecked,
    checkEmailExists,
    setUpForm,
    setUpSocialForm,
    checkPasswordReset,
} from '../common/utils';
import { startSIWATimer } from '../common/utils/siwaTimer';
import {
    setEmail,
    socialLogInUser,
    socialLogInSoftUser,
} from '../common/actions/auth';
import {
    RESET_PASSWORD_STATUS_PATH,
    SOFT_ACCOUNT_STATUS_PATH,
    LOG_IN_PATH,
    SIGN_UP_PATH,
    SOCIAL_SIGN_UP_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_LOG_IN_PATH,
    SOCIAL_LINK_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    CONFIRMATION_PATH,
    BASE_URL,
    RESET_PASSWORD_PATH,
    SET_PASSWORD_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
} from '../common/constants';

import Page from './Page';
import ConnectedSignInScreen from '../common/containers/signin/ConnectedSignInScreen';
import ConnectedStatusScreen from '../common/containers/status/ConnectedStatusScreen';
import ConnectedSetPasswordScreen from '../common/containers/setPassword/ConnectedSetPasswordScreen';
import ConnectedConfirmationScreen from '../common/containers/confirmation/ConnectedConfirmationScreen';
import ConnectedExistingAccountScreen from '../common/containers/socialLogin/ConnectedExistingAccountScreen';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eb/social-login-facebook';
import isEmpty from 'lodash/isEmpty';

const getRoutes = (pageInfo, store) => {
    const PageWithInfo = (props) => <Page {...pageInfo} {...props} />;

    const checkAccountStatus = (nextRouterState, replace) => {
        accountStatusChecked(store, replace);
    };

    const setUpSocialSingUpFormOnEnter = (nextRouterState, replace) => {
        setUpSocialForm(store, replace);
    };

    const setUpFormOnEnter = async ({ location: { query } }, replace) => {
        const {
            authentication: { socialAuthenticationData = {} } = {},
        } = store.getState();
        const {
            isUser,
            idToken,
            externalIdentities: { sign_in_methods, can_login } = {},
        } = socialAuthenticationData;

        if (query.email) {
            store.dispatch(setEmail(query.email));
        }

        if (isUser) {
            if (isEmpty(sign_in_methods)) {
                /* Soft accounts can't login directly because they don't have a
                 * password or any other sign in method.
                 * When following SIWA for soft accounts, and there are no sign in methods
                 * we ask them to create a password first.
                 * TODO:
                 * Metrics?
                 */
                store.dispatch(socialLogInSoftUser());

                startSIWATimer({ store });

                return replace(`${BASE_URL}/${SET_LINKED_SOFT_ACCOUNTS_PATH}`);
            } else if (
                sign_in_methods &&
                sign_in_methods.includes('apple_login')
            ) {
                await store.dispatch(
                    socialLogInUser(SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE, {
                        accessToken: idToken,
                        sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
                        skipCreation: false,
                    }),
                );
            }

            startSIWATimer({ store });

            replace(`${BASE_URL}/${SOCIAL_LINK_PATH}`);
        } else if (idToken) {
            startSIWATimer({ store });

            replace(`${BASE_URL}/${SOCIAL_LOOK_UP_PATH}`);
        }

        setUpForm(store);
    };

    const checkSocialToken = (nextRouterState, replace) => {
        const {
            authentication: { socialAuthenticationData: { idToken } = {} } = {},
        } = store.getState();

        if (!idToken) {
            replace(BASE_URL);
        }
    };

    const setUpLinkFormOnEnter = ({ location: { query } }, replace) => {
        const {
            authentication: { socialAuthenticationData: { email } } = {},
        } = store.getState();

        if (email) {
            store.dispatch(setEmail(email));
        }

        setUpForm(store);
    };

    const checkEmailOnEnter = (nextRouterState, replace) => {
        checkEmailExists(store, replace);
    };

    const checkResetOnEnter = ({ location: { query, pathname } }, replace) => {
        checkPasswordReset(store, query, replace, pathname);
    };

    return (
        <Route path={BASE_URL} component={PageWithInfo}>
            <IndexRoute
                component={ConnectedSignInScreen}
                onEnter={setUpFormOnEnter}
            />

            <Route
                path={CONFIRMATION_PATH}
                component={ConnectedConfirmationScreen}
            />
            <Route
                path={LOG_IN_PATH}
                component={ConnectedSignInScreen}
                onEnter={checkAccountStatus}
            />
            <Route
                path={SIGN_UP_PATH}
                component={ConnectedSignInScreen}
                onEnter={checkAccountStatus}
            />
            <Route
                path={SOCIAL_SIGN_UP_PATH}
                component={ConnectedSignInScreen}
                onEnter={setUpSocialSingUpFormOnEnter}
            />
            <Route
                path={SOCIAL_LOOK_UP_PATH}
                component={ConnectedExistingAccountScreen}
                onEnter={checkSocialToken}
            />
            <Route
                path={SOCIAL_LOG_IN_PATH}
                component={ConnectedSignInScreen}
                onEnter={checkAccountStatus}
            />
            <Route
                path={SOCIAL_LINK_PATH}
                component={ConnectedSignInScreen}
                onEnter={setUpLinkFormOnEnter}
            />
            <Route
                path={LINK_ACCOUNTS_LOG_IN_PATH}
                component={ConnectedSignInScreen}
                onEnter={checkAccountStatus}
            />
            <Route
                path={SET_LINKED_SOFT_ACCOUNTS_PATH}
                component={ConnectedSignInScreen}
                onEnter={checkEmailOnEnter}
            />
            <Route
                path={RESET_PASSWORD_PATH}
                component={ConnectedSetPasswordScreen}
                onEnter={checkResetOnEnter}
            />
            <Route
                path={SET_PASSWORD_PATH}
                component={ConnectedSetPasswordScreen}
                onEnter={checkResetOnEnter}
            />
            <Route path="status">
                <Route
                    path={SOFT_ACCOUNT_STATUS_PATH}
                    component={ConnectedStatusScreen}
                />
                <Route
                    path={RESET_PASSWORD_STATUS_PATH}
                    component={ConnectedStatusScreen}
                />
            </Route>
        </Route>
    );
};

export default getRoutes;
